<template>
  <div class="animated fadeIn">
    <div class="row">

      <div class="col-xl">
        <b-card>
          <template slot="header">
            <div class="form-row">
              <div class="form-group col-md">
                  <treeselect ref="structureTreeselect"
                              v-model="structureValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.StructureFilter.Placeholder')"
                              :options="structureItems"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              @close="loadData"
                              @input="filterInput">
                  </treeselect>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md">
                  <treeselect ref="waveTreeselect"
                              v-model="waveValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.WaveFilter.Placeholder')"
                              :options="waveItems"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :flat="true"
                              :defaultExpandLevel="1"
                              :disable-branch-nodes="true"
                              @close="loadData"
                              @input="filterInput">
                  </treeselect>
              </div>
              <div class="form-group col-md">
                  <treeselect ref="scenarioTreeselect"
                              v-model="scenarioValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.ScenarioFilter.Placeholder')"
                              :options="scenarioItems"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :flat="true"
                              :defaultExpandLevel="1"
                              :disable-branch-nodes="true"
                              @close="loadData"
                              @input="filterInput">
                  </treeselect>
              </div>
              <div class="form-group col-md">
                <treeselect ref="propertyTreeselect"
                            v-model="propertyValue"
                            :auto-load-root-options="false"
                            :placeholder="$t('Webportal.PropertyValueFilter.Placeholder')"
                            :options="propertyItems"
                            :disableFuzzyMatching="true"
                            :disabled="propertySelectDisabled"
                            :multiple="true"
                            :showCount="true"
                            showCountOf="LEAF_DESCENDANTS"
                            :flat="true"
                            :defaultExpandLevel="1"
                            :disable-branch-nodes="true"
                            @close="loadData"
                            @input="filterInput">

                </treeselect>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md">
                <search-input v-model.trim="searchText"
                             :placeholder="$t('Webportal.SearchTextFilter.Placeholder')"
                             @change="loadData">
                </search-input>
              </div>
              <div class="form-group col-md">
                <div class="input-group d-flex justify-content-end">
                  <b-pagination v-model="selectedPage"
                                :total-rows="totalCount"
                                :per-page="pageSize"
                                :limit="5"
                                :disabled="isExporting"
                                @change="getSurveyInstances"
                                class="mb-0"></b-pagination>
                    <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ShowAllChecks')">
                      <b-button variant="primary"
                                :disabled="getAllSurveyInstancesDisabled"
                                @click="getAllSurveyInstances">
                        {{ $n(this.totalCount) }}
                      </b-button>
                    </span>
                </div>
              </div>
              <div class="form-group col-md-auto">
                <!-- For VW AG demo only -->
                <!-- <b-button variant="primary"
                        :disabled="isExporting || downloadDealerReportsDisabled"
                        v-b-tooltip.hover.nofade="toolTipDelay"
                        title="Bericht freigeben">
                  <font-awesome-icon icon="cloud-upload" />
                </b-button>
                <b-button variant="primary"
                        :disabled="isExporting || downloadDealerReportsDisabled"
                        v-b-tooltip.hover.nofade="toolTipDelay"
                        title="Reklamation">
                  <font-awesome-icon icon="exclamation-circle" />
                </b-button> -->
                <b-button-group>
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.DownloadDealerReports')">
                    <b-button variant="primary"
                              :disabled="isExporting || downloadDealerReportsDisabled"
                              @click="downloadDealerReports()">
                      <font-awesome-icon icon="file-pdf" />
                    </b-button>
                  </span>

                  <b-dropdown id="ReportLanguageDropdown" right variant="outline-primary"
                              v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.CheckReportLanguage.Description')">
                    <template slot="button-content">
                      <font-awesome-icon :icon="['far', useSurveyDefaultLocalization ? 'globe' : 'language']" fixed-width />
                    </template>
                    <b-dropdown-item @click="useSurveyDefaultLocalization = true">
                      <font-awesome-icon :icon="['far', 'globe']" fixed-width />
                      {{ $t('Webportal.SurveyDefaultLanguage') }}
                      <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.SurveyDefaultLanguage.Description')">
                        <font-awesome-icon :icon="['fas', 'info-circle']" />
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="useSurveyDefaultLocalization = false">
                      <font-awesome-icon :icon="['far', 'language']" fixed-width />
                      {{ $t('Webportal.LocationLanguage') }}
                      <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.LocationLanguage.Description')">
                        <font-awesome-icon :icon="['fas', 'info-circle']" />
                      </span>
                    </b-dropdown-item>
                  </b-dropdown>
                </b-button-group>
              </div>
            </div>
          </template>

          <div v-bind:class="{ spinner: isLoading || isExporting }" style="min-height:300px;">
            <!-- Important: use @click.stop and @click.native.stop for action items inside table
                https://bootstrap-vue.js.org/docs/components/table/ -->
            <b-table small hover responsive :fixed="browserIsIE"
                    :foot-clone="items && items.length >= 5"
                    :items="items"
                    :fields="fields"
                    no-local-sorting
                    @sort-changed="sortChanged"
                    @row-clicked="rowClicked">
              <template v-slot:head(Select)>
                <b-form-checkbox v-model="toggleSelectionChecked"
                                :indeterminate="toggleSelectionIndeterminate"
                                style="min-height:1.0rem"
                                @click.native.stop
                                @change="toggleSelection"
                                v-b-tooltip.hover.nofade="toolTipDelay"
                                :title="$t('Webportal.SelectAll')" />
              </template>
              <template v-slot:head(Survey.Title)>{{ $t('Webportal.Survey') }}</template>
              <template v-slot:head(SurveyInstance.ExternalSurveyInstanceId)>{{ $t('Webportal.SurveyInstanceId') }}</template>
              <template v-slot:head(ClientLocation.ClientLocationIdOnClientSite)>{{ $t('Webportal.LocationId') }}</template>
              <template v-slot:head(ClientLocation)>{{ $t('Webportal.LocationAddress') }}</template>
              <template v-slot:head(SurveyInstance.TimeStampSurveyStart)>{{ $t('Webportal.CalendarWeek') }}</template>
              <template v-slot:head(SurveyInstance.Result)>{{ $t('Webportal.Result') }}</template>
              <template v-slot:head(SurveyInstance.SurveyInstanceStatusId)>{{ $t('Webportal.Status') }}</template>

              <template slot="top-row">
                <td></td>
                <td>
                  <search-input small v-model.trim="columnFilter.Survey.Title" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.SurveyInstance.ExternalSurveyInstanceId" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.ClientLocation.ClientLocationIdOnClientSite" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.ClientLocation.FullAddress" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td v-if="isCalendarWeekFieldVisible">
                  <search-input small v-model.trim="columnFilter.SurveyInstance.CalendarWeek" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.SurveyInstance.Result" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td v-if="isStatusFieldVisible" colspan="2">
                  <search-select small v-model="columnFilter.SurveyInstance.SurveyInstanceStatusId"
                                :options="surveyInstanceStatusOptions" @change="loadData"
                                :placeholder="$t('Webportal.SearchValueFilter.Placeholder')" />
                </td>
                <td v-else></td>
              </template>

              <template v-slot:cell(Select)="data">
                <b-form-checkbox v-model="data.item.Selected"
                                :disabled="!isSurveyInstanceProcessed(data.item)"
                                @click.native.stop
                                @change="updateToggleSelection" />
              </template>
              <template v-slot:cell(Survey.Title)="data">
                {{ data.value }}
              </template>
              <template v-slot:cell(SurveyInstance.ExternalSurveyInstanceId)="data">
                {{ data.value }}
              </template>
              <template v-slot:cell(ClientLocation.ClientLocationIdOnClientSite)="data">
                {{ data.value }}<br>
                <span v-b-tooltip.hover.nofade="toolTipDelay" :title="getLocationActiveText()">
                  <font-awesome-icon v-if="data.item.ClientLocation.IsActive"
                                    :icon="['far', 'lightbulb-on']" />
                </span>
                <span v-b-tooltip.hover.nofade="toolTipDelay" :title="getLocationInactiveText()">
                  <font-awesome-icon v-if="!data.item.ClientLocation.IsActive"
                                    :icon="['far', 'lightbulb-slash']" />
                </span>
              </template>
              <template v-slot:cell(ClientLocation)="data">
                {{ data.value.Name }}<br>
                {{ data.value.Address }}<br>
                {{ data.value.City }}, {{ data.value.PostalCode }}
              </template>
              <template v-slot:cell(SurveyInstance.TimeStampSurveyStart)="data">
                {{ calendarWeekAndDateFormatted(data.item) }}
              </template>
              <template v-slot:cell(SurveyInstance.Result)="data">
                {{ (data.value != null) ? $n(data.value) + '%' : '' }}
              </template>
              <template v-slot:cell(SurveyInstance.SurveyInstanceStatusId)="data">
                <span v-b-tooltip.hover.nofade="toolTipDelay" :title="getStatusText(data.item.SurveyInstance)">
                  <font-awesome-icon 
                    :icon="getStatusIcon(data.item.SurveyInstance)"
                    class="ml-2" 
                    style="min-width: 20px;" />
                </span>
                <span v-b-tooltip.hover.nofade="toolTipDelay" :title="getLastSubmittedPerEMailFormatted(data.item.SurveyInstance)" >
                  <font-awesome-icon 
                    :icon="getEnvelopeIcon(data.item.SurveyInstance)"
                    v-if="data.item.SurveyInstance.IsVisibleForEmailProcessing"
                    class="ml-1" 
                    style="min-width: 20px; color: #AAA;" />
                </span>
              </template>
              <template v-slot:cell(Action)="data">
                <span v-if="!isMsmUser" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ShowPreview')">
                  <b-button pill variant="primary" size="sm"
                            @click.stop="showPreview(data.item, useSurveyDefaultLocalization)"
                            :disabled="!isSurveyInstanceProcessed(data.item)">
                    <span class="text-nowrap">
                      <font-awesome-icon icon="eye" />&nbsp;
                      <span :class="['flag-icon', `flag-icon-${getSurveyInstanceLanguage(data.item, useSurveyDefaultLocalization)}`]" />
                    </span>
                  </b-button>
                </span>
                <b-dropdown v-else
                            split right variant="primary" size="sm"
                            @click.stop="showPreview(data.item, useSurveyDefaultLocalization)"
                            v-b-tooltip.hover.nofade="toolTipDelay"
                            :title="$t('Webportal.ShowPreview')"
                            :disabled="!isSurveyInstanceProcessed(data.item)">
                  <template slot="button-content">
                    <span class="text-nowrap">
                      <font-awesome-icon icon="eye" fixed-width />&nbsp;
                      <span :class="['flag-icon', `flag-icon-${getSurveyInstanceLanguage(data.item, useSurveyDefaultLocalization)}`]" />
                    </span>
                  </template>
                  <b-dropdown-text style="width: 300px;">{{ $t('Webportal.AvailableCheckReports') }}</b-dropdown-text>
                  <b-dropdown-header>{{ $t('Webportal.SurveyDefaultLanguage') }}</b-dropdown-header>
                  <b-dropdown-item v-for="targetGroup in data.item.TargetGroups" :key="`SDL${targetGroup}`"
                                   @click="showPreview(data.item, true, targetGroup)">
                    <span :class="['flag-icon', `flag-icon-${getSurveyInstanceLanguage(data.item, true)}`]" />
                    {{ $t('Webportal.TargetGroup') }} {{ targetGroup }}
                  </b-dropdown-item>
                  <b-dropdown-header>{{ $t('Webportal.LocationLanguage') }}</b-dropdown-header>
                  <b-dropdown-item v-for="targetGroup in data.item.TargetGroups" :key="`LL${targetGroup}`"
                                   @click="showPreview(data.item, false, targetGroup)">
                    <span :class="['flag-icon', `flag-icon-${getSurveyInstanceLanguage(data.item, false)}`]" />
                    {{ $t('Webportal.TargetGroup') }} {{ targetGroup }}
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
          </div>
        </b-card>
      </div>

      <div class="col-xl-5 p-0 pdf-viewer-container-xxl" v-bind:class="{ 'd-none': !isPreviewVisible }">
        <div class="card pdf-viewer col-5 p-0">
          <div>
            <button type="button" class="close" aria-label="Close" @click="closePreview()"
                    v-b-tooltip.hover.nofade="toolTipDelay"
                    :title="$t('Webportal.ClosePreview')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="card-body pdf-viewer-height pt-0" v-bind:class="{ spinner: isPreviewLoading }">
            <iframe class="pdf-viewer-height w-100" :src="previewUrl" frameBorder="0"></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppConfig from '@/mixins/AppConfig'
import DownloadFileMixin from '@/mixins/DownloadFileMixin'

import SearchInput from '@/components/SearchInput'
import SearchSelect from '@/components/SearchSelect'

import EventBus from '@/services/EventBus.js';
import SurveysService from '@/services/SurveysService'
import ReportsService from '@/services/ReportsService'
import AuthenticationService from '@/services/AuthenticationService'
import AdministationService from '@/services/AdministationService';

const PAGE_SIZE_DEFAULT = 20;
const PAGE_SIZE_MAXIMUM = 999999;

export default {
  name: "CheckExplorer",

  components: { SearchInput, SearchSelect },

  mixins: [ AppConfig, DownloadFileMixin ],

  data () {
    return {
      isLoading: false,                     // Determines if data loading is currently in progress

      // Filter properties
      clientSettings: null,
      structureItems: [],                   // Data items for structure treeselect
      structureValue: [],                   // Selected structure items
      waveItems: [],                        // Data items for wave treeselect
      waveValue: [],                        // Selected wave items
      scenarioItems: [],                    // Data items for scenarios
      scenarioValue: [],                    // Selected scenarios
      propertyItems: [],                    // Data items for property treeselect
      propertyValue: [],                    // Selected property items
      searchText: null,                     // Full text search text

      columnFilter: {                       // Filter values for each column
        Survey: { Title: null },
        SurveyInstance : {
          ExternalSurveyInstanceId: null,
          CalendarWeek: null,
          Result: null,
          SurveyInstanceStatusId: null },
        ClientLocation: {
          ClientLocationIdOnClientSite: null,
          FullAddress: null }
      },

      sortBy: null,                         // Sorting column
      sortDescending: false,                // Sorting order

      currentStructureValue: [],            // Current selected structure items for change detection
      currentFilter: null,                  // Current filter object for change detection

      isExporting: false,

      // Paging properties
      pageSize: PAGE_SIZE_DEFAULT,          // Number of items on one page of the table
      selectedPage: 1,                      // Number of the page, that is currently visible
      totalCount: 0,                        // Number of total items

      useSurveyDefaultLocalization: true,

      // Table properties
      items: [],
      fields: [
        { key: 'Select', thStyle: 'width:2em' },
        { key: 'Survey.Title', thStyle: '', sortable: true },
        { key: 'SurveyInstance.ExternalSurveyInstanceId', thStyle: 'width:7em', sortable: true },
        { key: 'ClientLocation.ClientLocationIdOnClientSite', thStyle: 'width:8em', sortable: true },
        { key: 'ClientLocation', thStyle: '', sortable: true },
        { key: 'SurveyInstance.TimeStampSurveyStart', class: 'text-center', thStyle: 'width:11em', sortable: true },
        { key: 'SurveyInstance.Result', class: 'text-right', thStyle: 'width:6em', sortable: true },
        { key: 'SurveyInstance.SurveyInstanceStatusId', thStyle: 'width:5em', thClass: 'text-left', tdClass: 'text-left', sortable: true },
        { key: 'Action', label: '', thStyle: 'width:6em', tdClass: 'text-right' }
      ],
      isCalendarWeekFieldVisible: true,
      isStatusFieldVisible: true,

      toggleSelectionChecked: false,
      toggleSelectionIndeterminate: false,

      previewUrl: null,
      isPreviewLoading: false,
      isPreviewVisible: false,
      previewCancelToken: null,
      surveyInstancesCancelToken: null,

      toolTipDelay: { delay: { "show": 500, "hide": 100 } }
    }
  },

  computed: {
    filter: function() {
      // Creates a new object without empty values from source
      const isNotEmptyPredicate = o => !_.isEmpty(o);
      const pickByDeep = (obj, fn) =>
        _.pickBy( // Remove on root level
          _.mapValues(obj, (val, key) =>
            !_.isObjectLike(val) ? val : // Take plain values
              !_.isEmpty(_.pickBy(val, fn)) ? _.pickBy(val, fn) : null) // Map values of nested object
          , fn)

      let result = {
        clientIds: [],
        projectIds: [],
        surveyIds: [],
        waveIds: this.waveValue,
        scenarioIds: this.scenarioValue,
        propertyIds: this.propertyValue,
        searchText: this.searchText,
        columnFilter: pickByDeep(this.columnFilter, isNotEmptyPredicate),
        sortBy: this.sortBy,
        sortDescending: this.sortDescending
      };

      // Evaluate structure value items
      _.forEach(this.structureValue, structureItem => {
        let id = structureItem.substring(1);

        switch (structureItem[0]) {
          case 'C': result.clientIds.push(id); break;
          case 'P': result.projectIds.push(id); break;
          case 'S': result.surveyIds.push(id); break;
        }
      });

      return result;
    },

    options: function() {
      let result = {
        page: this.selectedPage,
        pageSize: this.pageSize
      }

      return result;
    },

    propertySelectDisabled: function () {
      return _.isEmpty(this.structureValue);
    },

    getAllSurveyInstancesDisabled: function () {
      return this.totalCount <= PAGE_SIZE_DEFAULT || this.totalCount >= 1000 || this.pageSize === PAGE_SIZE_MAXIMUM;
    },

    selectedSurveyInstanceIds: function () {
      return _.map(this.items.filter(item => item.Selected), 'SurveyInstance.SurveyInstanceId');
    },

    downloadDealerReportsDisabled: function () {
      return _.isEmpty(this.items) || _.isEmpty(this.selectedSurveyInstanceIds);
    },

    isMsmUser: function() {
      return AuthenticationService.isMSMUser;
    },

    surveyInstanceStatusOptions: function() {
      let result = [
          { value: '220', text: this.$t('Webportal.StatusOK') }
      ]

      if (this.isMsmUser)
      {
        result.push({ value: '300', text: this.$t('Webportal.StatusDeleted') });
        result.push({ value: '<=215', text: this.$t('Webportal.StatusProcessing') });
      }

      return result;
    },
  },

  mounted() {
    this.getClientSettings();
    this.getSurveyStructure();
    this.getSurveyWaves();
    this.getSurveyScenarios();
    this.loadData();

    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeDestroy() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    getLocationActiveText() {
      return this.$t('Webportal.Location') + ' ' + this.$t('Webportal.Active');
    },
    getLocationInactiveText() {
      return this.$t('Webportal.Location') + ' ' + this.$t('Webportal.Inactive');
    },

    getStatusIcon(item) {
      let retVal = 'check';
      if (item && item.SurveyInstanceStatusId <= 215) {
        retVal = 'cogs';
      } else if (item && item.SurveyInstanceStatusId === 300) {
        retVal = 'trash-alt';
      }
      return retVal;
    },

    getEnvelopeIcon(item) {
      let retVal = '';
      if (item && item.LastSubmittedPerEMail) {
        retVal = 'envelope';
      } else {
        retVal = 'envelope-open';
      }
      return retVal;
    },

    getStatusText(item) {
      let retVal = '';
      if (item && item.SurveyInstanceStatusId <= 215) {
        retVal = this.$t('Webportal.StatusProcessing');
      } else if (item && item.SurveyInstanceStatusId === 300) {
        retVal = this.$t('Webportal.StatusDeleted');
      } else {
        retVal = this.$t('Webportal.StatusOK');
      }
      return retVal;
    },

    getLastSubmittedPerEMailFormatted(item) {
      let retVal = this.$t('Webportal.MailWillBeSent');
      if (item.LastSubmittedPerEMail) {
        retVal = this.$t('Webportal.MailSent') + ' ' + this.$options.filters.msm_date(item.LastSubmittedPerEMail);
      }
      return retVal;
    },

    getSurveyInstanceLanguage(item, useSurveyDefault) {
      return useSurveyDefault === false &&
        item.ClientLocation.DefaultLocalization &&
        item.ClientLocation.DefaultLocalization.includes('-') ?
        item.ClientLocation.DefaultLocalization.split('-')[1].toLowerCase() :
        item.Survey.DefaultLocalization.split('-')[1].toLowerCase();
    },

    localizationChanged(localizationId) {
      this.getSurveyStructure();
      this.getSurveyWaves();
      this.getSurveyInstances();
      this.getSurveyScenarios();
    },

    getClientSettings() {
      AdministationService.getClientSettings()
        .then(response => {
          this.clientSettings = response.data ? new Map(response.data.map(item => [item.ClientId, item])) : null;

          // Show calendar week column only if one of the clients the user has permission for is configured to show check date or time
          this.isCalendarWeekFieldVisible = this.isMsmUser || response.data && response.data.some(cs => cs.ShowCheckDate || cs.ShowCheckTime);
          if (!this.isCalendarWeekFieldVisible)
            this.fields.find(f => f.key == 'SurveyInstance.TimeStampSurveyStart').class += " d-none";

          // Show status column only if one of the clients the user has permission for is configured to show it
          this.isStatusFieldVisible = this.isMsmUser || response.data && response.data.some(cs => cs.ShowCheckStatus);
          if (!this.isStatusFieldVisible)
            this.fields.find(f => f.key == 'SurveyInstance.SurveyInstanceStatusId').class += " d-none";
        });
    },

    // Get survey structure data for the structure treeselect
    getSurveyStructure() {
      SurveysService.getSurveyStructure()
        .then(response => {
          this.structureItems = response.data;
        })
    },

    // Get survey structure data for the structure treeselect
    getSurveyWaves() {
      SurveysService.getSurveyWaves(this.filter)
        .then(response => {
          this.waveItems = response.data;
        })
    },

    getSurveyScenarios() {
      SurveysService.getSurveyScenarios(this.filter)
        .then(response => {
          this.scenarioItems = response.data;
        })
    },

    // Get survey structure data for the structure treeselect
    getSurveyProperties() {
      if (!this.propertySelectDisabled)
      {
        SurveysService.getSurveyCustomProperties(this.filter, true)
          .then(response => {
            this.propertyItems = response.data;
          })
      }
    },

    // Gets survey instances data for the table from the server, including current filter and pagination
    getSurveyInstances(page) {
      this.isLoading = true;

      if (page) this.selectedPage = page;

      // Cancel previous request
      if (this.surveyInstancesCancelToken) {
        this.surveyInstancesCancelToken.cancel();
      }

      this.surveyInstancesCancelToken = axios.CancelToken.source();

      SurveysService.getSurveyInstances(this.filter, this.options, this.surveyInstancesCancelToken)
        .then(response => {
          this.totalCount = response.data.TotalCount;
          this.items = response.data.items;
          this.isLoading = false;
          this.toggleSelectionChecked = false;
          this.toggleSelectionIndeterminate = false;
        })
        .catch((thrown) => {
          // TODO
        });
    },

    getAllSurveyInstances() {
      this.selectedPage = 1;
      this.pageSize = PAGE_SIZE_MAXIMUM;
      this.getSurveyInstances();
    },

    // Applies changed filter and triggeres data loading if necessary
    loadData() {
      // Only load data when effective filter has changed
      // TODO: The order of array elements shouldn't matter when comparing
      if (!_.isEqual(this.filter, this.currentFilter)) {
        let structureValueChanged = !_.isEqual(this.currentStructureValue, this.structureValue);
        this.currentStructureValue = this.structureValue;
        if (structureValueChanged)
        {
          this.waveValue = [];
          this.propertyValue = [];
          this.scenarioValue = [];
        }

        this.currentFilter = this.filter;

        this.selectedPage = 1;
        this.pageSize = PAGE_SIZE_DEFAULT;

        this.getSurveyInstances();
        
        if (structureValueChanged)
        {
          this.getSurveyWaves();
          this.getSurveyProperties();
          this.getSurveyScenarios();
        }
      }
    },

    // Called whenever the input of a treeselect control changes
    filterInput(value) {
      // Workaround: Only call data loading when clear all is pressed
      if (_.size(value) === 0)
        this.loadData();
    },

    isSurveyInstanceProcessed(item) {
      return item.SurveyInstance.SurveyInstanceStatusId > 215 &&
             item.SurveyInstance.SurveyInstanceStatusId < 300
    },

    sortChanged(e) {
      this.sortBy = e.sortBy;
      this.sortDescending = e.sortDesc;
      this.loadData();
    },

    // Selects a row when clicked
    rowClicked(item) {
      if (!this.isSurveyInstanceProcessed(item))
          return;

      let checked = item.Selected ? !item.Selected : true;

      this.updateToggleSelection(checked);

      // Use $set to register the new property for vue tracking
      this.$set(item, 'Selected', checked);
    },

    toggleSelection(checked) {
      _.forEach(this.items, item => {
          if (this.isSurveyInstanceProcessed(item)) {
            this.$set(item, 'Selected', checked);
          }
        });
    },

    updateToggleSelection(checked) {
      let count = 0;

      _.forEach(this.items, item => {
          if (item.Selected) count++;
        });

      // New value is not written to property right now
      checked ? count++ : count--;

      // Handle changes in individual flavour checkboxes
      if (count === 0) {
        this.toggleSelectionIndeterminate = false;
        this.toggleSelectionChecked = false;
      } else if (count === this.items.length) {
        this.toggleSelectionIndeterminate = false;
        this.toggleSelectionChecked = true;
      } else {
        this.toggleSelectionIndeterminate = true;
        this.toggleSelectionChecked = false;
      }
    },

    downloadDealerReports() {
      this.isExporting = true;
      ReportsService.getCheckReports(this.selectedSurveyInstanceIds, { useSurveyDefaultLocalization: this.useSurveyDefaultLocalization })
        .then(response => {
          this.downloadFile(response);
          this.isExporting = false;
        })
        .catch(_ => { this.isExporting = false; } );
    },

    showPreview(item, useSurveyDefault, targetGroup) {
      let options = { 
        localizationId: !useSurveyDefault ? item.ClientLocation.DefaultLocalization : null,
        targetGroup: targetGroup
      };

      // Check for pdf-viewer-breakpoint
      if (window.innerWidth < 1600)
      {
        // Open in new tab
        var win = window.open('', '_blank');
        win.document.title = `Check Report ${item.SurveyInstance.ExternalSurveyInstanceId}`;
        win.focus();

        ReportsService.getCheckReport(item.SurveyInstance.SurveyInstanceId, options)
          .then((response) => {
            const blobUrl = window.URL.createObjectURL(response.data);
            win.location.href = `${process.env.BASE_URL}static/pdfjs/web/viewer.html?file=${blobUrl}`;
          });
      }
      else
      {
        // Open in preview window
        if (this.previewCancelToken)
          this.previewCancelToken.cancel('showPreview');

        this.previewCancelToken = axios.CancelToken.source();

        this.isPreviewLoading = true;
        this.isPreviewVisible = true;

        ReportsService.getCheckReport(item.SurveyInstance.SurveyInstanceId, options, this.previewCancelToken)
          .then((response) => {
            this.isPreviewLoading = false;

            const blobUrl = window.URL.createObjectURL(response.data);
            this.previewUrl =`${process.env.BASE_URL}static/pdfjs/web/viewer.html?file=${blobUrl}`;
          })
          .catch((thrown) => {
            if (thrown.message != 'showPreview')
            {
              this.previewUrl = null;
              this.isPreviewLoading = false;
            }
          });
      }
    },

    closePreview() {
      this.isPreviewLoading = false;
      this.isPreviewVisible = false;
      this.previewUrl = null;

      if (this.previewCancelToken)
        this.previewCancelToken.cancel('closePreview');
    },

    calendarWeekAndDateFormatted(item) {
      let cs = this.clientSettings ? this.clientSettings.get(item.Client.ClientId) : null;

      if (this.isMsmUser || (cs && cs.ShowCheckDate && cs.ShowCheckTime))
        return item.SurveyInstance.CalendarWeek + ' (' + this.$options.filters.msm_datetime(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else if (cs && cs.ShowCheckDate)
        return item.SurveyInstance.CalendarWeek + ' (' + this.$options.filters.msm_date(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else if (cs && cs.ShowCheckTime)
        return item.SurveyInstance.CalendarWeek + ' (' + this.$options.filters.msm_time(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else
        return item.SurveyInstance.CalendarWeek
    }
  }
};
</script>